/**
 * 선택된 심볼 상태를 관리하는 Pinia 스토어
 * - 현재 선택된 심볼 및 관련 시세/메타 정보를 제공합니다.
 * - 심볼 변경 시 구독 관리와 이벤트 알림을 수행합니다.
 * - 종목 선택 시 당일 고가/저가를 자동으로 로드합니다.
 */
import { useSymbolSubscriptionManager } from '@/composables/useSymbolSubscriptionManager';
import { useMarketDataStore, useAccountStore, useAssetStore } from '@template/stores';
import { ALL_SYMBOLS, type TradingSymbol } from '@template/types';
import { getFirstAvailableSymbolCode } from '@template/utils';
import { getWebSocketService } from '@/services/websocket';
import { defineStore, storeToRefs } from 'pinia';
import { quoteService } from '@/services/api';
import { computed, readonly, ref } from 'vue';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export const useSelectedSymbolStore = defineStore('selectedSymbol', () => {
  const marketDataStore = useMarketDataStore();
  const accountStore = useAccountStore();
  const subscriptionManager = useSymbolSubscriptionManager();
  const wsService = getWebSocketService();

  // 실제 사용 가능한 종목 목록에서 첫 번째 종목을 기본값으로 사용
  // 초기화 시점에는 availableSymbolCodes가 비어있을 수 있으므로 빈 문자열로 초기화
  const selectedSymbol = ref<string>('');
  const connectionStatus = ref<ConnectionStatus>('disconnected');
  const symbolsCache = ref<TradingSymbol[]>([]);
  const symbolChangeListeners = new Set<(symbol: string) => void>();

  let initializing = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const loadSymbolsCache = async () => {
    if (symbolsCache.value.length === 0) {
      symbolsCache.value = ALL_SYMBOLS;
    }
  };

  const updateChartSymbolSubscription = (symbol: string) => {
    // 차트 구독 관련 로직은 향후 필요 시 구현
    // console.log(`[useSelectedSymbolStore] 선택된 심볼: ${symbol}`);
  };

  const safeInitialize = async () => {
    if (initializing) {
      return;
    }

    initializing = true;
    try {
      connectionStatus.value = 'connecting';
      await loadSymbolsCache();

      // 주의: selectedSymbol 자동 설정 제거
      // 차트 레이아웃이 로드된 후 첫 번째 차트의 종목이 선택되도록 함
      // selectedSymbol이 비어있어도 자동으로 첫 번째 종목을 설정하지 않음

      // TODO: 실제 환경에서는 WebSocket 연결 상태를 확인 후 연결
      connectionStatus.value = 'connected';
      updateChartSymbolSubscription(selectedSymbol.value);

      // 초기화 시 선택된 종목의 고가/저가 로드 (종목이 있는 경우만)
      if (selectedSymbol.value) {
        await loadDailyHighLow(selectedSymbol.value);
      }
    } catch (error) {
      console.warn('[useSelectedSymbolStore] 초기화 실패 - 재시도 예정', error);
      connectionStatus.value = 'disconnected';

      retryTimer = setTimeout(() => {
        initializing = false;
        safeInitialize().catch((err) => {
          console.error('[useSelectedSymbolStore] 재시도 중 오류', err);
        });
      }, 100);
      return;
    } finally {
      if (!retryTimer) {
        initializing = false;
      }
    }
  };

  const initialize = async () => {
    if (connectionStatus.value === 'connected') {
      return;
    }
    await safeInitialize();
  };

  // 지연 초기화
  setTimeout(() => {
    safeInitialize().catch((error) => {
      console.error('[useSelectedSymbolStore] 초기화 중 오류', error);
    });
  }, 0);

  const { marketData } = storeToRefs(marketDataStore);

  const selectedSymbolData = computed(() => {
    return marketDataStore.getSymbolDataBySymbol(selectedSymbol.value);
  });

  const currentPrice = computed(() => selectedSymbolData.value?.sellPrice ?? 0);

  const changePercent = computed(() => {
    const value = selectedSymbolData.value?.changePercent;
    // NaN이거나 유효하지 않은 경우 null 반환 (표시 시 '-'로 처리)
    return typeof value === 'number' && !isNaN(value) ? value : null;
  });

  const changeValue = computed(() => selectedSymbolData.value?.change ?? 0);

  const highPrice = computed(() => selectedSymbolData.value?.high ?? 0);

  const lowPrice = computed(() => selectedSymbolData.value?.low ?? 0);

  const buyPrice = computed(() => {
    const symbolData = selectedSymbolData.value;
    return symbolData?.buyPrice ?? 0;
  });

  const sellPrice = computed(() => {
    const symbolData = selectedSymbolData.value;
    return symbolData?.sellPrice ?? 0;
  });

  const symbolInfo = computed(() => {
    return symbolsCache.value.find((symbol) => symbol.ticker === selectedSymbol.value);
  });

  const symbolType = computed(() => symbolInfo.value?.type || 'forex');

  const symbolDescription = computed(() => symbolInfo.value?.description || selectedSymbol.value);

  const getSelectedSymbol = () => selectedSymbol.value;

  /**
   * 종목의 당일 고가/저가를 API에서 로드
   * 이미 고가/저가가 있으면 스킵 (WebSocket으로 업데이트된 경우 자동 스킵)
   */
  const loadDailyHighLow = async (stockCd: string) => {
    if (!stockCd) return;

    try {
      const accountNo = accountStore.selectedAccountNoSafe;
      await marketDataStore.loadDailyHighLow(stockCd, quoteService, accountNo);
    } catch (error) {
      console.error(`[useSelectedSymbolStore] ${stockCd} 당일 고가/저가 로드 실패:`, error);
    }
  };

  const setSelectedSymbol = async (symbol: string) => {
    const previous = selectedSymbol.value;
    selectedSymbol.value = symbol;
    updateChartSymbolSubscription(symbol);

    if (previous !== symbol) {
      symbolChangeListeners.forEach((listener) => listener(symbol));
      // console.log(`🔄 선택된 심볼 변경: ${previous} → ${symbol}`);

      // 종목 변경 시 당일 고가/저가 자동 로드
      await loadDailyHighLow(symbol);

      // TODO: [DEBUG] 시세 주기 출력 - 추후 제거 예정
      // 종목 변경 시 시세 정보 로그 출력
      try {
        // logQuoteData가 public 메서드이므로 호출 가능
        if (typeof (wsService as any).logQuoteData === 'function') {
          (wsService as any).logQuoteData('symbolChange');
        }
      } catch (error) {
        // WebSocketService를 사용할 수 없는 경우 무시 (정상적인 상황일 수 있음)
        // console.error('[useSelectedSymbolStore] WebSocketService 로그 출력 실패:', error);
      }
    }
  };

  const onSymbolChange = (listener: (symbol: string) => void) => {
    symbolChangeListeners.add(listener);
    return () => symbolChangeListeners.delete(listener);
  };

  const unsubscribeAll = () => {
    subscriptionManager.unsubscribeAllSymbols();
  };

  const isConnected = computed(() => wsService.isConnected());

  return {
    // 상태
    selectedSymbol: readonly(selectedSymbol),
    selectedSymbolData,
    symbolInfo,
    marketData,
    isConnected,
    connectionStatus: readonly(connectionStatus),

    // 가격 정보
    currentPrice,
    changePercent,
    changeValue,
    buyPrice,
    sellPrice,
    highPrice,
    lowPrice,

    // 심볼 정보
    symbolType,
    symbolDescription,

    // 제어 함수
    initialize,
    getSelectedSymbol,
    setSelectedSymbol,
    onSymbolChange,
    unsubscribeAll,
  };
});
